// Font family configuration
export const fontFamily = {
  regular: 'DMSans-Regular',
} as const;

// Font size configuration
export const fontSize = {
  xxs: 12,
  xs: 14,
  sm: 16,
  md: 18,
  lg: 20,
  xl: 24,
  xxl: 32,
  xxxl: 36,
} as const;

// Font weight configuration
export const fontWeight = {
  regular: '400',
  medium: '500',
  semiBold: '600',
  bold: '700',
} as const;

// Type definitions for better TypeScript support
export type FontSize = keyof typeof fontSize;
export type FontWeight = keyof typeof fontWeight;
export type FontFamily = keyof typeof fontFamily;

// Utility function to get font style object
export const getFontStyle = (
  size: FontSize,
  weight: FontWeight = 'regular',
) => ({
  fontFamily: fontFamily.regular,
  fontSize: fontSize[size],
  fontWeight: fontWeight[weight],
  lineHeight: fontSize[size] + 4,
});

// Predefined text styles for common use cases
export const textStyles = {
  // Small text styles, 12px
  xxsRegular: getFontStyle('xxs', 'regular'),
  xxsMedium: getFontStyle('xxs', 'medium'),
  xxsSemiBold: getFontStyle('xxs', 'semiBold'),
  xxsBold: getFontStyle('xxs', 'bold'),

  // Body text styles, 14px
  xsRegular: getFontStyle('xs', 'regular'),
  xsMedium: getFontStyle('xs', 'medium'),
  xsSemiBold: getFontStyle('xs', 'semiBold'),
  xsBold: getFontStyle('xs', 'bold'),

  // Large text styles, 16px
  smRegular: getFontStyle('sm', 'regular'),
  smMedium: getFontStyle('sm', 'medium'),
  smSemiBold: getFontStyle('sm', 'semiBold'),
  smBold: getFontStyle('sm', 'bold'),

  // Title text styles, 18px
  mdRegular: getFontStyle('md', 'regular'),
  mdMedium: getFontStyle('md', 'medium'),
  mdSemiBold: getFontStyle('md', 'semiBold'),
  mdBold: getFontStyle('md', 'bold'),

  // Heading text styles, 20px
  lgRegular: getFontStyle('lg', 'regular'),
  lgMedium: getFontStyle('lg', 'medium'),
  lgSemiBold: getFontStyle('lg', 'semiBold'),
  lgBold: getFontStyle('lg', 'bold'),

  // Large heading text styles, 24px
  xlRegular: getFontStyle('xl', 'regular'),
  xlMedium: getFontStyle('xl', 'medium'),
  xlSemiBold: getFontStyle('xl', 'semiBold'),
  xlBold: getFontStyle('xl', 'bold'),

  // Extra large heading text styles, 32px
  xxlRegular: getFontStyle('xxl', 'regular'),
  xxlMedium: getFontStyle('xxl', 'medium'),
  xxlSemiBold: getFontStyle('xxl', 'semiBold'),
  xxlBold: getFontStyle('xxl', 'bold'),

  // Extra extra large heading text styles, 36px
  xxxlRegular: getFontStyle('xxxl', 'regular'),
  xxxlMedium: getFontStyle('xxxl', 'medium'),
  xxxlSemiBold: getFontStyle('xxxl', 'semiBold'),
  xxxlBold: getFontStyle('xxxl', 'bold'),
} as const;

export const mobileTypographyStyles = {
  display: {
    // 32px
    ...textStyles.xxlBold,
    lineHeight: textStyles.xxlBold.fontSize * 1.2,
  },
  header1: {
    // 24px
    ...textStyles.xlBold,
  },
  header1R: {
    // 24px
    ...textStyles.xlRegular,
  },
  header2: {
    // 20px
    ...textStyles.lgBold,
  },
  header3: {
    // 18px
    ...textStyles.mdSemiBold,
  },
  header4: {
    // 16px
    ...textStyles.smSemiBold,
    lineHeight: textStyles.smSemiBold.fontSize * 1.2,
  },
  header5: {
    // 16px
    ...textStyles.smRegular,
  },
  body: {
    // 16px
    ...textStyles.smRegular,
    lineHeight: textStyles.smRegular.fontSize * 1.2,
  },
  bodySB: {
    // 16px
    ...textStyles.smSemiBold,
    lineHeight: textStyles.smSemiBold.fontSize * 1.2,
  },
  label: {
    // 14px
    ...textStyles.xsRegular,
    lineHeight: textStyles.xsRegular.fontSize * 1.2,
  },
  labelSB: {
    // 14px
    ...textStyles.xsSemiBold,
  },
  microLabel: {
    // 12px
    ...textStyles.xxsMedium,
  },
  microLabelSB: {
    // 12px
    ...textStyles.xxsSemiBold,
  },
  lgButton: {
    // 16px
    ...textStyles.smSemiBold,
  },
  rgButton: {
    // 14px
    ...textStyles.xsSemiBold,
  },
  toggleButton: {
    // 14px
    ...textStyles.xsMedium,
  },
  linkRegular: {
    // 16px
    ...textStyles.smSemiBold,
    lineHeight: textStyles.smSemiBold.fontSize * 1.2,
  },
  linkSmall: {
    // 14px
    ...textStyles.xsSemiBold,
  },
} as const;
